<template>
  <div>
    <IconButton
      v-if="isMobileDevice"
      class="absolute top-4 right-4 z-30"
      variant="text"
      size="sm"
      aria-label="Go back"
      @click="router.back()"
    >
      <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
    </IconButton>

    <div v-else class="absolute flex items-center gap-2 px-4 pt-4 z-30">
      <IconButton
        variant="text"
        size="sm"
        aria-label="Go back"
        @click="router.back()"
      >
        <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
      </IconButton>
    </div>

    <div
      class="flex flex-col items-center h-full px-16 justify-center"
    >
      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center p-12">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>

      <!-- User Profile Card -->
      <div v-else-if="user" class="bg-surface rounded-2xl p-8 shadow-lg relative justify-end">
        <button 
          class="absolute right-4 top-4 px-4 py-2 bg-error/80 text-white rounded-lg hover:bg-error transition-colors" 
          @click="handleSignOut"
        >
          Sign Out
        </button>
        <div class="flex items-center gap-6">
          <div v-if="user.user_metadata?.avatar_url" class="relative">
            <img 
              :src="user.user_metadata.avatar_url" 
              alt="Profile" 
              class="w-24 h-24 rounded-full object-cover border-4 border-primary"
            />
          </div>
          <div v-else class="w-24 h-24 rounded-full bg-primary/20 flex justify-center items-center">
            <span class="text-3xl font-bold text-primary">{{ userInitials }}</span>
          </div>
          
          <div>
            <h2 class="text-2xl font-bold">{{ userName }}</h2>
            <p class="text-text-secondary">{{ user.email }}</p>
          </div>
        </div>
        <div class="flex flex-col mt-8 gap-3 px-6">
          <div v-if="appUser" class="flex justify-between items-center">
            <span class="text-sm text-text-secondary">Role</span>
            <span class="font-medium capitalize">{{ appUser.role }}</span>
          </div>

          <div v-if="appUser?.role === 'student'" class="flex justify-between items-center gap-3">
            <span class="text-sm text-text-secondary">Difficulties</span>
            <span class="font-medium text-right">{{ allowedDifficultyNames.length ? allowedDifficultyNames.join(', ') : '—' }}</span>
          </div>
        </div>
        <div class="mt-8 pt-8 gap-x-2 border-t border-surface-hover flex justify-between items-center">
          <div>
            <h3 class="text-error font-semibold">Danger Zone</h3>
            <p class="text-sm text-text-secondary">Once you delete your account, there is no going back. Please be certain.</p>
          </div>
          <button 
            class="px-4 py-2 border border-error text-error rounded-lg hover:bg-error hover:text-text-primary transition-all"
            @click="showDeleteDialog = true"
          >
            Delete Account
          </button>
        </div>
      </div>

      <!-- Delete Account Dialog -->
      <AccountDeleteConfirmDialog
        v-model:is-visible="showDeleteDialog"
        :is-loading="deleting"
        @confirm="handleDeleteAccount"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { User } from '@supabase/supabase-js'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useSupabaseAuth } from '../composables/useSupabaseAuth'
import { useToast } from '../composables/useToast'
import { useDeviceDetection } from '@/composables/useDeviceDetection'
import AccountDeleteConfirmDialog from '@/components/AccountDeleteConfirmDialog.vue'
import IconButton from '@/components/IconButton.vue'
import { ChevronLeftIcon } from '@heroicons/vue/24/outline'
import { useConvexQuery } from 'convex-vue'
import { api } from '../../convex/_generated/api'

const { isMobileDevice } = useDeviceDetection();
const router = useRouter()
const { user: authUser, signOut, deleteAccount } = useSupabaseAuth()
const { createToast } = useToast()
const loading = ref(true)
const deleting = ref(false)
const showDeleteDialog = ref(false)
const user = ref<User>()

const { data: appUser } = useConvexQuery(api.users.getCurrentUser, {})
const { data: allDifficulties } = useConvexQuery(api.difficulties.list, {})

const allowedDifficultyNames = computed(() => {
  const allowed = new Set(appUser.value?.allowedDifficulties ?? [])
  return (allDifficulties.value ?? []).filter((d) => allowed.has(d._id)).map((d) => d.name)
})

onMounted(() => {
  // Initialize user data from the auth hook
  user.value = authUser.value
  loading.value = false
})

const userName = computed<string>(() => {
  if (!user.value) return ''
  return user.value.user_metadata?.full_name || 
         user.value.user_metadata?.name || 
         user.value.email?.split('@')[0] || 
         'User'
})

const userInitials = computed(() => {
  const name = userName.value
  return name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase() || 'U'
})

const handleSignOut = async () => {
  try {
    await signOut()
    createToast({
      type: 'success',
      message: 'You have been signed out'
    })
    router.push('/')
  } catch (error) {
    createToast({
      type: 'error',
      message: 'Failed to sign out'
    })
  }
}

const handleDeleteAccount = async () => {
  try {
    deleting.value = true
    await deleteAccount()
    showDeleteDialog.value = false
    createToast({
      type: 'success',
      message: 'Your account has been deleted'
    })
    router.push('/')
  } catch (error) {
    console.error('Error deleting account:', error)
    createToast({
      type: 'error',
      message: 'Failed to delete account. Please try again.'
    })
  } finally {
    deleting.value = false
  }
}
</script> 
